// Respostas guardadas (PT e EN): criar, editar, apagar.
import { useApi } from "@ecliptica/api-client/react";
import { CANNED_BODY, cannedComplete } from "@ecliptica/contexts-support";
import { contentLangs, useTranslation } from "@ecliptica/i18n";
import { Button, Card, CardContent, Dialog, DialogContent, DialogFooter, DialogTitle, EmptyState, Field, Label, PageHeader, Textarea, toast } from "@ecliptica/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, MessageSquareText, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useCan } from "../../features/auth/RequireStaff";

type Canned = { id: number; title: string; bodyPt: string; bodyEn: string };
const EMPTY = { title: "", bodyPt: "", bodyEn: "" };

export function CannedPage() {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const can = useCan();
  const langs = contentLangs(); // só as línguas ativas se mostram e editam
  const [editing, setEditing] = useState<(typeof EMPTY & { id?: number }) | null>(null);
  const list = useQuery({ queryKey: ["canned"], queryFn: async () => (await (await api.v1.admin.support.canned.$get()).json()) as Canned[] });

  async function save() {
    if (!editing) return;
    // Vai o texto de todas as línguas, incluindo o das inativas tal como estava (não se mostra, mas não se apaga).
    const json = { title: editing.title, bodyPt: editing.bodyPt, bodyEn: editing.bodyEn };
    const res = editing.id
      ? await api.v1.admin.support.canned[":id"].$put({ param: { id: String(editing.id) }, json })
      : await api.v1.admin.support.canned.$post({ json });
    if (!res.ok) return toast.error(t("error.generic"));
    setEditing(null);
    await qc.invalidateQueries({ queryKey: ["canned"] });
  }
  async function remove(id: number) {
    await api.v1.admin.support.canned[":id"].$delete({ param: { id: String(id) } });
    await qc.invalidateQueries({ queryKey: ["canned"] });
  }

  return (
    <>
      <Link to="/support" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {t("nav.support")}
      </Link>
      <PageHeader
        title={t("desk.canned")}
        description={t("desk.cannedPage.description")}
        actions={
          can("support.reply") && (
            <Button variant="primary" onClick={() => setEditing({ ...EMPTY })}>
              <Plus /> {t("desk.cannedPage.new")}
            </Button>
          )
        }
      />
      {!list.data?.length ? (
        <EmptyState icon={<MessageSquareText />} title={t("desk.cannedPage.empty")} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {list.data.map((c) => (
            <Card key={c.id}>
              <CardContent className="flex flex-col gap-2 p-4">
                <p className="font-semibold text-gold">{c.title}</p>
                {langs.map((l) => (
                  <p key={l} className="line-clamp-3 whitespace-pre-wrap text-sm">
                    {langs.length > 1 && <span className="text-xs text-muted-foreground">{l.toUpperCase()} · </span>}
                    {c[CANNED_BODY[l]]}
                  </p>
                ))}
                {can("support.reply") && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(c)}>
                      {t("desk.cannedPage.edit")}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void remove(c.id)}>
                      {t("desk.cannedPage.delete")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogTitle>{editing?.id ? t("desk.cannedPage.edit") : t("desk.cannedPage.new")}</DialogTitle>
          {editing && (
            <div className="flex flex-col gap-3">
              <Field label={t("desk.cannedPage.title")} maxLength={80} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              {langs.map((l) => CANNED_BODY[l]).map((k) => (
                <div key={k} className="flex flex-col gap-1.5">
                  <Label htmlFor={k}>{t(`desk.cannedPage.${k}`)}</Label>
                  <Textarea id={k} maxLength={4000} className="min-h-28" value={editing[k]} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              {t("action.cancel")}
            </Button>
            <Button variant="primary" disabled={!editing?.title.trim() || !cannedComplete(editing)} onClick={() => void save()}>
              {t("action.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
